// Point d'entrée principal de la bibliothèque Universal UI

// React 18 compatibility patch for custom element event listeners (onChange, onUiChange, etc.)
if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  const originalDispatch = HTMLElement.prototype.dispatchEvent;
  HTMLElement.prototype.dispatchEvent = function(event) {
    const result = originalDispatch.call(this, event);
    if (this.tagName && this.tagName.toLowerCase().startsWith('ui-')) {
      const type = event.type;
      const onType = 'on' + type;
      const onChangeCamel = 'on' + type.charAt(0).toUpperCase() + type.slice(1);
      
      if (typeof this[onType] === 'function') {
        this[onType](event);
      } else if (typeof this[onChangeCamel] === 'function') {
        this[onChangeCamel](event);
      }
      
      // Support custom names, e.g., ui-change -> onUiChange / onui-change
      if (type.includes('-')) {
        const camelEvent = 'on' + type.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
        if (typeof this[camelEvent] === 'function') {
          this[camelEvent](event);
        }
      }
    }
    return result;
  };
}

import { UISelect, UIOption } from './components/select.js';
import { UIDatePicker } from './components/datepicker.js';
import { UIDateRangePicker } from './components/daterangepicker.js';
import { UITimePicker } from './components/timepicker.js';
import { UINumber } from './components/number.js';
import { UISwitch } from './components/switch.js';
import { UICheckbox } from './components/checkbox.js';
import { UISlider } from './components/slider.js';
import { UIRating } from './components/rating.js';
import { UIMonthPicker } from './components/monthpicker.js';
import { UIYearPicker } from './components/yearpicker.js';
import { UIColorPicker } from './components/colorpicker.js';
import { UIUpload } from './components/upload.js';
import { UIAutocomplete } from './components/autocomplete.js';
import { UICascader } from './components/cascader.js';
import { UITagInput } from './components/taginput.js';
import { UIOtp } from './components/otp.js';
import { UIPassword } from './components/password.js';
import { UISignature } from './components/signature.js';
import { UITransfer } from './components/transfer.js';

// Injection dynamique des variables de style CSS et de la police de caractères
if (typeof document !== 'undefined') {
  const styleId = 'universal-ui-theme-variables';
  if (!document.getElementById(styleId)) {
    // Importer la police d'écriture élégante
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    // Injecter les variables CSS globales
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      :root {
        --ui-font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        
        --ui-primary: #6366f1;
        --ui-primary-hover: #4f46e5;
        --ui-primary-rgb: 99, 102, 241;
        
        --ui-bg: #ffffff;
        --ui-surface: #ffffff;
        --ui-surface-hover: #f9fafb;
        --ui-surface-active: #f3f4f6;
        
        --ui-text: #1f2937;
        --ui-text-muted: #6b7280;
        --ui-text-light: #9ca3af;
        --ui-text-on-primary: #ffffff;
        
        --ui-border: #e5e7eb;
        --ui-border-hover: #d1d5db;
        --ui-border-focus: #6366f1;
        
        --ui-success: #10b981;
        --ui-danger: #ef4444;
        --ui-warning: #f59e0b;
        
        --ui-focus-ring: rgba(99, 102, 241, 0.15);
        
        --ui-radius-sm: 6px;
        --ui-radius-md: 10px;
        --ui-radius-lg: 16px;
        --ui-radius-full: 9999px;
        
        --ui-input-height: 44px;
        --ui-padding-x: 14px;
        --ui-font-size-base: 14px;
        --ui-font-size-sm: 12px;
        --ui-font-size-lg: 16px;
        
        --ui-shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        --ui-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.08);
        --ui-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.08);
        --ui-shadow-popover: 0 12px 28px 0 rgba(0, 0, 0, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.04);
        
        --ui-transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
        --ui-transition-normal: 250ms cubic-bezier(0.4, 0, 0.2, 1);
        
        --ui-z-dropdown: 1000;
        --ui-z-popover: 1010;
        --ui-z-tooltip: 1020;
      }

      [data-theme="dark"] {
        --ui-bg: #090a0f;
        --ui-surface: #12131a;
        --ui-surface-hover: #1b1d28;
        --ui-surface-active: #222533;
        
        --ui-text: #f3f4f6;
        --ui-text-muted: #9ca3af;
        --ui-text-light: #6b7280;
        
        --ui-border: #222533;
        --ui-border-hover: #31354a;
        --ui-border-focus: #818cf8;
        
        --ui-primary: #818cf8;
        --ui-primary-hover: #6366f1;
        --ui-primary-rgb: 129, 140, 248;
        
        --ui-focus-ring: rgba(129, 140, 248, 0.25);
        
        --ui-shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
        --ui-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.3);
        --ui-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.35), 0 4px 6px -4px rgba(0, 0, 0, 0.35);
        --ui-shadow-popover: 0 12px 28px 0 rgba(0, 0, 0, 0.4), 0 2px 4px 0 rgba(0, 0, 0, 0.2);
      }
    `;
    document.head.appendChild(style);
  }
}

export { UISelect, UIOption, UIDatePicker, UIDateRangePicker, UITimePicker, UINumber, UISwitch, UICheckbox, UISlider, UIRating, UIMonthPicker, UIYearPicker, UIColorPicker, UIUpload, UIAutocomplete, UICascader, UITagInput, UIOtp, UIPassword, UISignature, UITransfer };
